<!--搜索条件容器-->
<template>
  <div class="sc-cont">
    <slot name="top" />
    <div
      v-if="!noShortcut && shortcutDatas.length"
      ref="commandRef"
      class="command"
    >
      <span class="label">已保存筛选条件：</span>
      <div ref="shortcutsRef" class="shortcuts">
        <li
          v-for="(item, index) of shortcutDatas"
          :key="item.name"
          :class="{ active: activeIndex == index, 'search-list': true }"
          @click.self="queryShortcut(index)"
          >{{ item.name }}
          <el-popconfirm
            confirm-button-text="是"
            cancel-button-text="否"
            :icon="WarningFilled"
            icon-color="#FF8218"
            title="是否删除?"
            popper-class="easy-search del-popper"
            @confirm="delShortcut(index)"
          >
            <template #reference>
              <el-icon class="deleteIcon" color="#898B8F">
                <CircleCloseFilled />
              </el-icon>
            </template>
          </el-popconfirm>
        </li>
      </div>

      <!-- 更多选项 -->
      <el-popover
        placement="bottom-start"
        :offset="12"
        trigger="hover"
        :show-arrow="false"
        :teleported="false"
        popper-class="more-popover"
      >
        <template #reference>
          <div
            v-show="alwaysShow || visibleCount < shortcutDatas.length"
            class="more"
          >
            +{{ shortcutDatas.length - visibleCount }}
          </div>
        </template>
        <template #default>
          <div class="innerbox">
            <div
              v-for="(item, index) of shortcutDatas.slice(visibleCount)"
              :key="item.name"
              :class="{
                activeItem: activeIndex == index + visibleCount,
                moreItem: true
              }"
            >
              <span
                class="more-pointer"
                @click.self="queryShortcut(index + visibleCount)"
              >
                {{ item.name }}
              </span>
              <div class="operate">
                <el-popconfirm
                  confirm-button-text="是"
                  cancel-button-text="否"
                  :icon="WarningFilled"
                  icon-color="#FF8218"
                  title="是否删除?"
                  :teleported="false"
                  popper-class="easy-search del-popper"
                  :persistent="true"
                  @confirm="delShortcut(index + visibleCount)"
                >
                  <template #reference>
                    <el-icon class="deleteIcon more-pointer" color="#898B8F">
                      <Icons.easySearchDelete />
                    </el-icon>
                  </template>
                </el-popconfirm>
                <el-icon
                  class="deleteIcon more-pointer"
                  color="#898B8F"
                  @click.stop="moveFirst(index + visibleCount)"
                >
                  <Icons.easySearchTop />
                </el-icon>
              </div>
            </div>
          </div>
        </template>
      </el-popover>
    </div>

    <draggable
      :key="condsKey"
      :class="{ cond: true, compact: compact }"
      :list="defaultSlot"
      item-key="key"
      draggable=".drag"
      @change="onOrderChange"
    >
      <template #item="{ element }">
        <component :is="element.component" :key="element.key" />
      </template>

      <template #footer>
        <div class="sf-query-btns">
          <SearchSubmitButton />
          <el-tooltip
            content="清除"
            placement="bottom"
            :fallback-placements="['top', 'bottom']"
          >
            <SearchResetButton class="sf-query-reset" />
          </el-tooltip>

          <el-tooltip
            v-if="!noShortcut"
            content="保存筛选"
            placement="bottom"
            :fallback-placements="['top', 'bottom']"
          >
            <el-button
              class="sf-query-save"
              :icon="Icons.easySearchSave"
              @click="createShortcut"
            />
          </el-tooltip>

          <SearchConditionConfig v-if="!noSetting" />
        </div>
      </template>
    </draggable>

    <slot name="bottom" />
  </div>
</template>

<script setup lang="ts">
/**
 * ### 查询条件容器组件
 */
import {
  nextTick,
  onMounted,
  ref,
  unref,
  computed,
  useSlots,
  VNode,
  watch,
  onBeforeUnmount
} from 'vue';
import { CircleCloseFilled, WarningFilled } from '@element-plus/icons-vue';
import { getLayout, saveLayout } from '../core/LayoutPersistent';
import { useShareData } from '../core/SharedData';
import { arrayMove, extract } from '../core/Common';
import { useDomMoreWatcher } from '../core/CommonInternal';
import { showDialog } from '../core/EasyPopup';
import { PlainObject } from '../core/types';
import { Icons } from '../common/Icons';
import Draggable from 'vuedraggable';

import _ from 'lodash-es';

import SearchSubmitButton from './button/SearchSubmitButton.vue';
import SearchResetButton from './button/SearchResetButton.vue';
import SearchConditionConfig from './internal/SearchConditionConfig.vue';
import SearchShortcutInputDialog from './internal/SearchShortcutInputDialog.vue';
import { uuid } from '../core/Common';

defineProps<{
  /**
   * ### 是否显示设置按钮
   */
  noSetting?: boolean;
  /**
   * ### 是否支持快捷筛选
   */
  noShortcut?: boolean;
}>();

// 子组件
const slots = useSlots();
const fields = (() => {
  let fields = slots.default?.() ?? [];
  // 分成三组
  let grouped = _.groupBy(fields, (e) => {
    if (_.isSymbol(e.type)) {
      // 注释
      return 'comment';
    }

    if (
      e.props &&
      (e.props.defaultHide || _.isString(e.props['default-hide']))
    ) {
      // 默认不显示的
      return 'hidden';
    }

    if (_.isObject(e.type) && (e.type as any).props?.noUi) {
      // 无界面的
      return 'hidden';
    }

    return 'visible'; // 需要显示的
  });

  // 无界面的放在后面
  return [...(grouped.visible || []), ...(grouped.hidden || [])];
})();

// 属性名可能是个数组, 第一个作为名字
function propName(name: any) {
  if (_.isArray(name)) return name[0];
  return name;
}

// 字段到index
const fieldIndexMap = new Map<VNode, number>();
fields.forEach((f, i) => fieldIndexMap.set(f, i));

// 搜索表单名
const searchName = useShareData('search', 'name');

// 加载布局
const layoutDatas = getLayout<{ id: string; show: boolean }[]>(
  'search',
  ['fields', searchName],
  [],
  (v) => !!v.push
);

// 排序
let sortedFields = _.sortBy(fields, (e) => {
  let name = propName(e.props?.name);
  let index = _.findIndex(layoutDatas, (i) => i.id == name);
  if (index < 0) return fieldIndexMap.get(e);
  return index;
});

interface SeachFieldItem {
  name: string;
  component: VNode;
  noUi: boolean;
  key: number | string;
}

function toVnodeComponents(vnodes: VNode[]): SeachFieldItem[] {
  return vnodes.map((v, index) => ({
    name: propName(v.props?.name),
    component: v,
    noUi: !!(v.type as any).props?.noUi, // 无ui的不需要drag
    key: uuid()
  }));
}

const defaultSlot = ref(toVnodeComponents(sortedFields));

if (slots.default) {
  watch(slots.default, (newfields) => {
    const currentComponents = defaultSlot.value;
    for (let f of newfields) {
      let name = propName(f.props?.name);
      const index = currentComponents.findIndex((e) => e.name == name);
      // 属性值发生变化
      if (
        index >= 0 &&
        !_.isEqual(currentComponents[index].component.props, f.props)
      ) {
        currentComponents[index].component.props = f.props;
        // 触发组件更新
        currentComponents[index].key = uuid();
      }
    }
  });
}

// 注册移动元素接口
const layout = useShareData('search', 'layout');
layout.control.$('move')((name: string, toIndex: number) => {
  const children = defaultSlot.value;

  const moveIndex = _.findIndex(children, (e) => e.name == name);

  arrayMove(sortedFields, moveIndex, toIndex);
  arrayMove(defaultSlot.value, moveIndex, toIndex);
});

const condsKey = ref(0);
// 重新加载条件
layout.control.$('reload')(() => {
  //layoutDatas = [];

  // 恢复排序
  defaultSlot.value = _.sortBy(defaultSlot.value, (e) => {
    let name = e.name;
    let vnode = fields.find((v) => propName(v.props?.name) == name)!;
    return fieldIndexMap.get(vnode);
  });

  // 触发重新更新
  ++condsKey.value;
});

layout.control.$('sorted')(() => {
  return defaultSlot.value
    .filter((e) => !e.noUi)
    .map((e) => ({
      name: e.name,
      label: e.component.props?.label
    }));
});

// 拖动移位置
function onOrderChange(evt: any) {
  const name = evt.moved.element.name;
  const fromIndex = evt.moved.oldIndex;
  const toIndex = evt.moved.newIndex;

  arrayMove(sortedFields, fromIndex, toIndex);

  layout.moveItem(name, toIndex);
}

////////////////////////////////////////////////////////////////
// 保存的快捷搜索
const shortcutDatas = ref(
  getLayout<{ name: string; params: PlainObject }[]>(
    'search',
    ['shortcut', searchName],
    [],
    (v) => !!v.push
  )
);

// 搜索表单名
const searchParams = useShareData('search', 'params');

// 快捷搜索区，是否显示more
const alwaysShow = ref(false);

// 判断是否将组件值保存到快捷筛选中
// hidden组件以及非searchCondition default slot中的组件均不保存
const isSaveToShortcut = (key: string) => {
  const result = defaultSlot.value.find((com: any) => {
    if (com.name == key) {
      return true;
    }
    // easy对于sf-sel-text等组件传入的name虽为数组 但组件名为数组第一项
    // 对于这种类型的组件 第二项的值也需保存 所以需从组件的props获取真实name做判断
    const propsName = com.component?.props?.name;
    if (Array.isArray(propsName) && propsName.includes(key)) {
      return true;
    }
    return false;
  });

  if (!result) {
    return false;
  }
  const componentType = result.component?.type as any;
  const componentName = componentType?.__name;
  if (componentName == 'SfHiddenParam') {
    return false;
  }
  return true;
};

// 过滤掉不需要保存到快捷搜索中的参数
const filterOutOuterParams = (params: Record<string, any>) => {
  const keys = Object.keys(params);
  for (const key of keys) {
    const result = isSaveToShortcut(key);
    if (!result) {
      Reflect.deleteProperty(params, key);
    }
  }
};

async function createShortcut() {
  let existItems = extract(shortcutDatas.value, 'name');

  let newSearch = await showDialog(
    SearchShortcutInputDialog,
    { existItems: existItems },
    {
      title: '保存为快捷筛选',
      customStyle: { width: '400px', height: '220px' }
    }
  );

  // 减少more闪烁
  alwaysShow.value = visibleCount.value !== shortcutDatas.value.length;

  let params = _.cloneDeep(_.omitBy(searchParams, (e) => e === undefined));
  filterOutOuterParams(params);
  shortcutDatas.value.unshift({ name: newSearch, params });

  nextTick(handleShortcutOverflow);

  saveShortcutLayout();
}

function delShortcut(index: number) {
  // 减少more闪烁
  alwaysShow.value = visibleCount.value !== shortcutDatas.value.length;

  shortcutDatas.value.splice(index, 1);

  nextTick(handleShortcutOverflow);

  saveShortcutLayout();
}

function moveFirst(index: number) {
  const item = shortcutDatas.value.splice(index, 1);
  shortcutDatas.value.unshift(...item);

  saveShortcutLayout();
}

function saveShortcutLayout() {
  saveLayout('search', ['shortcut', searchName], shortcutDatas.value);
}

// 当前搜索项
const activeIndex = ref(-1);
const setAndQuery = useShareData('search', 'set');
let activeParams: PlainObject | undefined;

// 分页参数
const pagination = useShareData('search', 'pagination');
const reset = useShareData('search', 'reset');
async function queryShortcut(index: number) {
  if (activeIndex.value === index) {
    // 取消选中 重制筛选项
    activeIndex.value = -1;
    pagination.page = 1;
    await reset();
    return;
  }
  let conditions = shortcutDatas.value[index];
  activeParams = conditions.params;
  // 快捷搜索时 将不在快捷搜索内的组件重置下
  const fieldInterfaceMap = layout.fieldInterfaceMap;
  for (let [_, item] of Object.entries(fieldInterfaceMap)) {
    if (!activeParams.hasOwnProperty(_) && isSaveToShortcut(_)) {
      (item as any).reset?.();
    }
  }
  await setAndQuery(activeParams);
  activeIndex.value = index;
  // fix bug
  // 当由A快捷筛选切换到B快捷筛选时 监听参数变化后会将activeParams置为undefined
  // 此时导致修改参数条件或者重置条件都将不会将选中的快捷筛选高亮去掉
  activeParams = conditions.params;
}

// 重新搜索时, 调整快捷搜索状态
watch(
  searchParams,
  (ps) => {
    filterOutOuterParams(activeParams || {});
    if (activeIndex.value >= 0 && !_.isMatch(unref(ps), activeParams!)) {
      activeParams = undefined;
      activeIndex.value = -1;
    }
  },
  { deep: true }
);

// 超长处理
const shortcutsRef = ref<HTMLElement>();
const commandRef = ref<HTMLElement>();
const visibleCount = ref(shortcutDatas.value.length);
let disconnectShortcut: (() => void) | undefined;

function handleShortcutOverflow() {
  if (!commandRef.value) return;

  disconnectShortcut?.();

  const boxList = shortcutsRef.value!.getElementsByClassName(
    'search-list'
  ) as unknown as HTMLElement[];
  const more = commandRef.value.querySelector('.more') as HTMLElement;

  const changedCallback = () => {
    const count = Array.from(boxList).filter(
      (item) => item.style.visibility === 'visible'
    ).length;
    visibleCount.value = count;

    alwaysShow.value = false;
  };

  disconnectShortcut = useDomMoreWatcher(
    {
      root: commandRef.value,
      threshold: [0, 1],
      rootMargin: '0px -40px 0px 0px'
    },
    boxList,
    more,
    changedCallback
  );
}

// 解决子元素宽度自适应问题
const compact = computed(() => {
  const length = defaultSlot.value.filter(
    (e) => !e.noUi && !layout.isHidden(e.name)
  ).length;

  return length <= 3;
});

// 滚动吸顶吸顶, 把marginBottom留给外部的container
const scrollContainer = useShareData('search', 'scrollContainer');
const marginBottom = computed(() => (scrollContainer.enable ? '0px' : '12px'));

onMounted(() => {
  handleShortcutOverflow();
});

onBeforeUnmount(() => {
  disconnectShortcut?.();
});
</script>

<style lang="scss" scoped>
.sc-cont {
  --sf-font-size: 14px;
  --sf-line-height: 34px;
  --sf-prefix-width: 100px;
  --sf-prefix-left-space: 4px;
  --sf-prefix-right-space: 6px;
  --sf-border-color: #dcdfe6;
  --sf-border-hover-color: #297ce6;
  --sf-border-radius: 4px;
  --el-input-border-radius: 2px;
  --el-color-primary-light-8: transparent;

  padding: 0px 12px;
  background-color: white;
  border-radius: 4px;
  margin: 0px var(--easy-page-padding-x);
  margin-bottom: v-bind(marginBottom);

  .cond {
    height: auto;
    // max-height: 65vh;
    overflow-y: hidden;
    padding: 12px 0px;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(172px, 1fr));
    grid-auto-flow: dense;
    gap: 8px;
    font-size: var(--sf-font-size);
    overflow-x: hidden;
  }

  .cond.compact {
    grid-template-columns: repeat(auto-fit, minmax(172px, 200px));
  }

  .sf-query-btns {
    display: flex;
    flex-direction: row;
    column-gap: 9px;
    flex-wrap: nowrap;
    place-content: flex-start;
  }

  .sf-query-reset,
  .sf-query-save {
    width: 34px;
    height: 34px;
    margin: 0 !important;
    border-radius: 4px;
    border: 1px solid #dcdcdc;
  }

  .sf-query-reset {
    width: 34px;
  }

  .sf-query-reset:hover {
    border-color: #2f7ff4;
    color: #2f7ff4;
  }

  .command {
    display: flex;
    align-items: center;
    box-sizing: border-box;
    margin: 0 var(--easy-page-padding-x);
    margin-top: 8px;
    font-size: 14px;

    .label {
      color: #7c7f85;
    }

    .shortcuts {
      display: flex;
      overflow: hidden;
      align-items: center;
      overflow: visible;

      li {
        position: relative;
        list-style: none;
        flex-shrink: 0;
        padding: 2px 4px;
        background: #f8f8f9;
        border-radius: 2px;
        border: 1px solid #e9e9ea;
        color: #242933;
        margin-right: 8px;

        .deleteIcon {
          position: absolute;
          top: -7px;
          right: -7px;
          display: none;
          font-size: 16px;
        }
      }

      li,
      span {
        cursor: pointer;

        &:hover {
          background-color: #eaf2fc;
        }
      }

      li:hover .deleteIcon {
        display: block;
      }

      .active {
        color: white !important;
        background-color: #327de8 !important;
      }
    }

    .more {
      flex-shrink: 0;
      line-height: 20px;
      border-radius: 2px;
      padding: 4px 5px;
      background: #eef0f5;
      color: #130f3c;
      cursor: pointer;
      //@include normalFont;
      &:hover {
        color: #2f7ff4;
      }
    }

    .txt-btn {
      color: #2f7ff4;
      font-size: 12px;
    }

    .txt-btn:hover {
      background-color: #eaf2fd;
    }

    :deep(.more-popover) {
      width: 180px !important;
      padding: 12px 0 !important;

      .innerbox {
        max-height: 200px;
        overflow-x: hidden;
        overflow-y: scroll;

        .moreItem {
          height: 32px;
          padding: 0 8px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex: 1;

          &:hover {
            background: #f3f4f6;
          }
        }

        .activeItem {
          color: #1656d9;
        }

        .more-pointer {
          cursor: pointer !important;
          //@include normalFont;
        }

        .deleteIcon:first-child {
          margin-right: 5px;
        }

        .operate {
          visibility: hidden;
        }

        .moreItem:hover .operate {
          visibility: visible;
        }
      }

      /**   滚动条样式  **/
      .innerbox::-webkit-scrollbar {
        width: 4px;
        height: 4px;
      }

      .innerbox::-webkit-scrollbar-thumb {
        border-radius: 5px;
        box-shadow: inset 0 0 5px rgba(0, 0, 0, 0.2);
        background: rgba(0, 0, 0, 0.2);
      }

      .innerbox::-webkit-scrollbar-track {
        box-shadow: inset 0 0 5px rgba(0, 0, 0, 0.2);
        border-radius: 0;
        background: rgba(0, 0, 0, 0.1);
      }
    }
  }
}

/*
@media (min-width: 1920px) {
    .sc-cont {
        .cond {
            grid-template-columns: repeat(7, 1fr);
        }
    }
}

@media (min-width: 1440px) {
    .sc-cont {
        .cond {
            grid-template-columns: repeat(6, 1fr);
        }
    }
} */
</style>
